import React from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Chart } from "primereact/chart";
import { useQuery } from "@tanstack/react-query";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import formService from "../../services/forms/form.service";
import { useToast } from "../../utils/toast";

const StratejikRapor = () => {
  const toast = useToast();
  const {
    data: raporData,
    refetch: refetchRapor,
    isLoading: raporResponseLoading
  } = useQuery({
    queryKey: ["stratejikRapor"],
    gcTime: 1000 * 60 * 11,
    refetchOnMount: false,
    queryFn: () => formService.stratejikAnketRapor()
  });

  const getChartData = () => {
    console.log("Getting chart data from:", raporData);
    if (!raporData?.result?.data?.stratejik_alan_puanlari) {
      console.log("No stratejik_alan_puanlari found");
      return null;
    }

    const data = raporData.result.data.stratejik_alan_puanlari;
    const labels = Object.keys(data).map((key) => {
      const alanAdi =
        {
          yesil_donusum: "Yeşil Dönüşüm",
          dijital_donusum: "Dijital Dönüşüm",
          egitim: "Eğitim",
          is_gelistirme: "İş Geliştirme",
          arge: "Ar-Ge",
          kapasite: "Kapasite Geliştirme",
          afet: "Afet Yönetimi"
        }[key] || key;
      return alanAdi;
    });

    const datasets = [
      {
        label: "Hazırlık",
        data: Object.values(data).map((item: any) => item.hazirlik),
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 2
      },
      {
        label: "Algı",
        data: Object.values(data).map((item: any) => item.algı),
        backgroundColor: "rgba(255, 99, 132, 0.2)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 2
      },
      {
        label: "Yetkinlik",
        data: Object.values(data).map((item: any) => item.yetkinlik),
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 2
      },
      {
        label: "İlerleme",
        data: Object.values(data).map((item: any) => item.ilerleme),
        backgroundColor: "rgba(255, 159, 64, 0.2)",
        borderColor: "rgba(255, 159, 64, 1)",
        borderWidth: 2
      },
      {
        label: "Ortalama",
        data: Object.values(data).map((item: any) => item.ortalama),
        backgroundColor: "rgba(153, 102, 255, 0.2)",
        borderColor: "rgba(153, 102, 255, 1)",
        borderWidth: 3,
        type: "line"
      }
    ];

    return {
      labels,
      datasets
    };
  };

  const getRadarChartData = () => {
    if (!raporData?.result?.data?.stratejik_alan_puanlari) return null;

    const data = raporData.result.data.stratejik_alan_puanlari;
    const labels = Object.keys(data).map((key) => {
      const alanAdi =
        {
          yesil_donusum: "Yeşil Dönüşüm",
          dijital_donusum: "Dijital Dönüşüm",
          egitim: "Eğitim",
          is_gelistirme: "İş Geliştirme",
          arge: "Ar-Ge",
          kapasite: "Kapasite Geliştirme",
          afet: "Afet Yönetimi"
        }[key] || key;
      return alanAdi;
    });

    return {
      labels,
      datasets: [
        {
          label: "Ortalama Puanlar",
          data: Object.values(data).map((item: any) => item.ortalama),
          backgroundColor: "rgba(54, 162, 235, 0.2)",
          borderColor: "rgba(54, 162, 235, 1)",
          borderWidth: 2,
          pointBackgroundColor: "rgba(54, 162, 235, 1)",
          pointBorderColor: "#fff",
          pointHoverBackgroundColor: "#fff",
          pointHoverBorderColor: "rgba(54, 162, 235, 1)"
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 3,

    scales: {
      y: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1
        }
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0
        }
      }
    },
    plugins: {
      legend: {
        position: "top" as const
      },
      title: {
        display: false
      }
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10
      }
    }
  };

  const radarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1
        }
      }
    },
    plugins: {
      legend: {
        position: "top" as const
      },
      title: {
        display: true,
        text: "Stratejik Alan Ortalama Puanları (Örümcek Ağı)"
      }
    }
  };

  if (raporResponseLoading) {
    return (
      <div className="flex justify-content-center">
        <ProgressSpinner />
      </div>
    );
  }

  if (!raporData?.result?.data) {
    return (
      <div className="flex justify-content-center">
        <Message severity="info" text="Henüz anket verisi bulunmamaktadır." />
      </div>
    );
  }

  const chartData = getChartData();
  const radarChartData = getRadarChartData();

  return (
    <div className="card">
      <div className="w-full surface-card">
        <div className="text-center mb-5">
          <div className="text-900 text-3xl font-medium mb-3">Stratejik Anket Raporu</div>
          <p className="text-600">OSB bazında stratejik alan puanlarının analizi ve görselleştirmesi</p>
        </div>

        {/* Yenile Butonu */}
        <Card title="Rapor Kontrolleri" className="mb-4">
          <div className="flex align-items-center gap-3">
            <Button label="Yenile" onClick={() => refetchRapor()} icon="pi pi-refresh" />
          </div>
        </Card>

        {/* İstatistikler */}
        <Card title="Genel İstatistikler" className="mb-4">
          <div className="grid">
            <div className="col-12 md:col-4">
              <div className="p-3 border-1 surface-border border-round text-center">
                <div className="text-2xl font-bold text-primary">
                  {raporData?.result?.data?.toplam_anket_sayisi ?? "—"}
                </div>
                <div className="text-600">Toplam Anket</div>
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="p-3 border-1 surface-border border-round text-center">
                <div className="text-2xl font-bold text-green-600">
                  {raporData?.result?.data?.osb_listesi?.length || 0}
                </div>
                <div className="text-600">OSB Sayısı</div>
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="p-3 border-1 surface-border border-round text-center">
                <div className="text-2xl font-bold text-blue-600">Tümü</div>
                <div className="text-600">Görünüm</div>
              </div>
            </div>
          </div>
        </Card>

        {/* Grafikler */}
        {chartData && (
          <Card title="Stratejik Alan Puanları (Çubuk Grafik)" className="mb-4">
            <div style={{ height: "400px", maxWidth: "100%", overflow: "hidden" }}>
              <Chart type="bar" data={chartData} options={chartOptions} />
            </div>
          </Card>
        )}

        {radarChartData && (
          <Card title="Stratejik Alan Ortalama Puanları (Örümcek Ağı)" className="mb-4">
            <div style={{ height: "400px" }}>
              <Chart type="radar" data={radarChartData} options={radarChartOptions} />
            </div>
          </Card>
        )}

        {/* Detaylı Puanlar */}
        <Card title="Detaylı Puanlar" className="mb-4">
          <div className="grid">
            {Object.entries(raporData?.result?.data?.stratejik_alan_puanlari || {}).map(
              ([key, values]: [string, any]) => {
                const alanAdi =
                  {
                    yesil_donusum: "Yeşil Dönüşüm",
                    dijital_donusum: "Dijital Dönüşüm",
                    egitim: "Eğitim",
                    is_gelistirme: "İş Geliştirme",
                    arge: "Ar-Ge",
                    kapasite: "Kapasite Geliştirme",
                    afet: "Afet Yönetimi"
                  }[key] || key;

                return (
                  <div key={key} className="col-12 md:col-6 lg:col-4">
                    <div className="p-3 border-1 surface-border border-round">
                      <h5 className="text-primary mb-3">{alanAdi}</h5>
                      <div className="grid">
                        <div className="col-6">
                          <div className="text-sm text-600">Hazırlık</div>
                          <div className="text-lg font-bold">{values.hazirlik.toFixed(1)}</div>
                        </div>
                        <div className="col-6">
                          <div className="text-sm text-600">Algı</div>
                          <div className="text-lg font-bold">{values.algı.toFixed(1)}</div>
                        </div>
                        <div className="col-6">
                          <div className="text-sm text-600">Yetkinlik</div>
                          <div className="text-lg font-bold">{values.yetkinlik.toFixed(1)}</div>
                        </div>
                        <div className="col-6">
                          <div className="text-sm text-600">İlerleme</div>
                          <div className="text-lg font-bold">{values.ilerleme.toFixed(1)}</div>
                        </div>
                        <div className="col-12">
                          <div className="text-sm text-600">Ortalama</div>
                          <div className="text-xl font-bold text-primary">{values.ortalama.toFixed(1)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default StratejikRapor;
